import {
  HttpClient,
  HttpParams
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  tap
} from 'rxjs';

import { API_ENDPOINTS } from '../constants/api-endpoints.constants';
import { ApiResponse } from '../models/api-response.model';
import { MenuStoreService } from './menu-store.service';
import { TokenDecodeService } from './token-decode.service';

export type BackendMenuStatus =
  'ACTIVE' | 'INACTIVE';

export type UiMenuStatus =
  'Active' | 'Inactive';

export type MenuPermissionScope =
  'ALL_ROLES' |
  'SYSTEM_ROLES' |
  'ADMIN_ONLY';

export interface Menu {
  id: string;
  menuName: string;
  displayName?: string;
  menuKey: string;

  parentMenuId?: string | null;
  parentName?: string | null;
  parentMenu?: string | null;

  routePath?: string | null;
  darkIcon?: string | null;
  whiteIcon?: string | null;

  sequence?: number | null;
  menuDescription?: string | null;

  isPermissionRequired?: boolean;
  forMenu?: boolean;

  permissionScope?: MenuPermissionScope;

  status?:
  BackendMenuStatus |
  UiMenuStatus |
  string;

  createdAt?: string;
  updatedAt?: string;

  subMenus?: Menu[];
}

export interface MenuPayload {
  menuName: string;
  menuKey: string;
  parentMenuId: string | null;
  routePath: string | null;
  darkIcon: string | null;
  whiteIcon: string | null;
  sequence: number | null;
  menuDescription: string | null;
  isPermissionRequired: boolean;
  forMenu: boolean;
  permissionScope: MenuPermissionScope;
  status: BackendMenuStatus;
}

export interface MenuItem {
  id: string;
  menuName: string;
  displayName: string;
  menuKey: string;

  routePath: string | null;

  parentMenuId: string | null;
  parentMenu: string | null;
  parent: string | null;

  order: number;
  sequence: number | null;

  darkIcon: string | null;
  lightIcon: string | null;
  whiteIcon: string | null;

  menuDescription: string | null;
  isPermissionRequired: boolean;
  forMenu: boolean;

  permissionScope: MenuPermissionScope;

  status: UiMenuStatus;

  createdAt?: string;
  updatedAt?: string;
}

export interface MenuPage {
  content: Menu[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface MenuPermission {
  menuId: string;
  id?: string;
  menuPermissionName: string;
  permissionKey: string;
  sequence?: number | null;
  menuPermissionDescription?: string | null;
  systemPermission?: boolean;
}

export interface MenuWithPermissions {
  menuId: string;
  menuName: string;
  menuKey: string;
  routePath?: string | null;
  menuIcon?: string | null;
  permissions: MenuPermission[];
  children: MenuWithPermissions[];
}

export interface MenuPermissionPage {
  menu: Menu;
  permissions: MenuPermission[];
}

export interface NavigationMenuItem {
  id: string;
  label: string;
  menuKey: string;
  route?: string;
  lightIcon: string;
  darkIcon: string;
  children?: NavigationMenuItem[];
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private myMenusRequest$?: Observable<Menu[]>;

  constructor(
    private http: HttpClient,
    private menuStore: MenuStoreService,
    private tokenService: TokenDecodeService
  ) { }

  get currentMenus$(): Observable<Menu[]> {
    return this.menuStore.menus$;
  }

  loadMyMenus(
    forceRefresh = false
  ): Observable<Menu[]> {
    const userId =
      this.tokenService.getUserId();

    if (!userId) {
      this.menuStore.clear();
      return of([]);
    }

    if (!forceRefresh) {
      const cached =
        this.menuStore.restore(userId);

      if (cached !== null) {
        return of(cached);
      }
    }

    if (this.myMenusRequest$) {
      return this.myMenusRequest$;
    }

    this.myMenusRequest$ = this.http
      .get<ApiResponse<Menu[]>>(
        API_ENDPOINTS.MENUS.MY_MENUS
      )
      .pipe(
        map(response =>
          response?.data || []
        ),
        tap(menus =>
          this.menuStore.set(
            userId,
            menus
          )
        ),
        finalize(() => {
          this.myMenusRequest$ = undefined;
        }),
        shareReplay({
          bufferSize: 1,
          refCount: false
        })
      );

    return this.myMenusRequest$;
  }

  clearMyMenus(): void {
    this.myMenusRequest$ = undefined;
    this.menuStore.clear();
  }

  toNavigationItems(
    menus: Menu[]
  ): NavigationMenuItem[] {
    return (menus || []).map(menu => ({
      id: menu.id,
      label:
        menu.displayName ||
        menu.menuName,
      menuKey: menu.menuKey,
      route:
        menu.routePath ||
        undefined,
      lightIcon:
        menu.darkIcon ||
        'assets/img/menu-dim.svg',
      darkIcon:
        menu.whiteIcon ||
        menu.darkIcon ||
        'assets/img/menu-light.svg',
      children:
        menu.subMenus?.length
          ? this.toNavigationItems(
            menu.subMenus
          )
          : undefined
    }));
  }

  getMenus(
    params: {
      search?: string;
      menuName?: string;
      menuKey?: string;
      routePath?: string;
      status?: string;
      parentMenuId?: string;
      sequence?: number | null;
      forMenu?: boolean;
      page?: number;
      size?: number;
      sort?: string;
    } = {}
  ): Observable<ApiResponse<MenuPage>> {
    let httpParams = new HttpParams();

    const values: Record<
      string,
      string |
      number |
      boolean |
      undefined
    > = {
      search: params.search,
      menuName: params.menuName,
      menuKey: params.menuKey,
      routePath: params.routePath,
      status: params.status
        ? this.toBackendStatus(
          params.status
        )
        : undefined,
      parentMenuId:
        params.parentMenuId,
      sequence:
        params.sequence ??
        undefined,
      forMenu:
        params.forMenu,
      page:
        (params.page || 1) - 1,
      size:
        params.size || 10,
      sort:
        params.sort
    };

    Object.entries(values)
      .forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== ''
        ) {
          httpParams =
            httpParams.set(
              key,
              String(value)
            );
        }
      });

    return this.http.get<
      ApiResponse<MenuPage>
    >(
      API_ENDPOINTS.MENUS.LIST,
      {
        params: httpParams
      }
    );
  }

  getParentMenus():
    Observable<ApiResponse<Menu[]>> {
    return this.http.get<
      ApiResponse<Menu[]>
    >(
      API_ENDPOINTS.MENUS.PARENT_MENU
    );
  }

  getMenuById(
    id: string
  ): Observable<ApiResponse<Menu>> {
    return this.http.get<
      ApiResponse<Menu>
    >(
      API_ENDPOINTS.MENUS.DETAILS(id)
    );
  }

  createMenu(
    menu: MenuPayload
  ): Observable<ApiResponse<null>> {
    return this.http.post<
      ApiResponse<null>
    >(
      API_ENDPOINTS.MENUS.CREATE,
      menu
    );
  }

  updateMenu(
    id: string,
    menu: MenuPayload
  ): Observable<ApiResponse<null>> {
    return this.http.put<
      ApiResponse<null>
    >(
      API_ENDPOINTS.MENUS.UPDATE(id),
      menu
    );
  }

  updateMenuStatus(
    id: string,
    status: BackendMenuStatus
  ): Observable<ApiResponse<null>> {
    return this.http.put<
      ApiResponse<null>
    >(
      API_ENDPOINTS.MENUS
        .UPDATE_STATUS(id),
      {
        status
      }
    );
  }

  deleteMenu(
    id: string
  ): Observable<ApiResponse<null>> {
    return this.http.delete<
      ApiResponse<null>
    >(
      API_ENDPOINTS.MENUS.DELETE(id)
    );
  }

  getAllMenuPermissions():
    Observable<
      ApiResponse<MenuPermission[]>
    > {
    return this.http.get<
      ApiResponse<MenuPermission[]>
    >(
      API_ENDPOINTS
        .MENU_PERMISSIONS.LIST
    );
  }

  getMenuPermissions(
    menuId: string
  ): Observable<MenuPermission[]> {
    return this.getAllMenuPermissions()
      .pipe(
        map(response =>
          (response.data || [])
            .filter(permission =>
              permission.menuId ===
              menuId
            )
        )
      );
  }

  getMenuPermissionById(
    id: string
  ): Observable<
    ApiResponse<MenuPermission>
  > {
    return this.http.get<
      ApiResponse<MenuPermission>
    >(
      API_ENDPOINTS
        .MENU_PERMISSIONS
        .DETAILS(id)
    );
  }

  createMenuPermission(
    permissions: MenuPermission[]
  ): Observable<ApiResponse<null>> {
    return this.http.post<
      ApiResponse<null>
    >(
      API_ENDPOINTS
        .MENU_PERMISSIONS.CREATE,
      permissions.map(permission =>
        this.toMenuPermissionPayload(
          permission
        )
      )
    );
  }

  updateMenuPermission(
    id: string,
    permission: MenuPermission
  ): Observable<ApiResponse<null>> {
    return this.http.put<
      ApiResponse<null>
    >(
      API_ENDPOINTS
        .MENU_PERMISSIONS
        .UPDATE(id),
      this.toMenuPermissionPayload(
        permission
      )
    );
  }

  deleteMenuPermission(
    id: string
  ): Observable<ApiResponse<null>> {
    return this.http.delete<
      ApiResponse<null>
    >(
      API_ENDPOINTS
        .MENU_PERMISSIONS
        .DELETE(id)
    );
  }

  getMenusWithPermissions():
    Observable<
      ApiResponse<
        MenuWithPermissions[]
      >
    > {
    return this.http.get<
      ApiResponse<
        MenuWithPermissions[]
      >
    >(
      API_ENDPOINTS
        .MENU_PERMISSIONS
        .MENUS_WITH_PERMISSIONS
    );
  }

  getMenuPermissionPage(
    menuId: string
  ): Observable<
    ApiResponse<MenuPermissionPage>
  > {
    return this.http.get<
      ApiResponse<MenuPermissionPage>
    >(
      API_ENDPOINTS.MENUS
        .MENU_PERMISSION_PAGE(
          menuId
        )
    );
  }

  transformToUI(
    menus: Menu[]
  ): MenuItem[] {
    const flatMenus =
      this.flattenMenus(menus);

    const names =
      new Map<string, string>();

    flatMenus.forEach(menu =>
      names.set(
        menu.id,
        menu.menuName
      )
    );

    return flatMenus.map(menu => ({
      id: menu.id,
      menuName: menu.menuName,
      displayName:
        menu.displayName ||
        menu.menuName,
      menuKey: menu.menuKey,
      routePath:
        menu.routePath ||
        null,
      parentMenuId:
        menu.parentMenuId ||
        null,
      parentMenu:
        menu.parentMenu ||
        null,
      parent:
        menu.parentName ||
        this.resolveParentName(
          menu,
          names
        ),
      order:
        Number(
          menu.sequence ||
          0
        ),
      sequence:
        menu.sequence ??
        null,
      darkIcon:
        menu.darkIcon ||
        null,
      lightIcon:
        menu.whiteIcon ||
        null,
      whiteIcon:
        menu.whiteIcon ||
        null,
      menuDescription:
        menu.menuDescription ||
        null,
      isPermissionRequired:
        menu.isPermissionRequired ??
        true,
      forMenu:
        menu.forMenu ??
        true,
      permissionScope:
        menu.permissionScope ||
        'ALL_ROLES',
      status:
        this.toUiStatus(
          menu.status
        ),
      createdAt:
        menu.createdAt,
      updatedAt:
        menu.updatedAt
    }));
  }

  flattenMenus(
    menus: Menu[],
    level = 0
  ): Menu[] {
    return (menus || [])
      .flatMap(menu => [
        {
          ...menu,
          displayName:
            `${'— '.repeat(level)}${menu.menuName}`
        },
        ...this.flattenMenus(
          menu.subMenus || [],
          level + 1
        )
      ]);
  }

  toMenuPayload(
    menu: Partial<Menu>
  ): MenuPayload {
    return {
      menuName:
        menu.menuName?.trim() ||
        '',
      menuKey:
        this.normalizeMenuKey(
          menu.menuKey ||
          menu.menuName ||
          ''
        ),
      parentMenuId:
        menu.parentMenuId ||
        menu.parentMenu ||
        null,
      routePath:
        menu.routePath?.trim() ||
        null,
      darkIcon:
        menu.darkIcon?.trim() ||
        null,
      whiteIcon:
        menu.whiteIcon?.trim() ||
        null,
      sequence:
        menu.sequence !== undefined &&
          menu.sequence !== null
          ? Number(
            menu.sequence
          )
          : null,
      menuDescription:
        menu.menuDescription?.trim() ||
        null,
      isPermissionRequired:
        menu.isPermissionRequired ??
        true,
      forMenu:
        menu.forMenu ??
        true,
      permissionScope:
        menu.permissionScope ||
        'ALL_ROLES',
      status:
        this.toBackendStatus(
          menu.status
        )
    };
  }

  toMenuPermissionPayload(
    permission: MenuPermission
  ): MenuPermission {
    return {
      menuId:
        permission.menuId,
      id:
        permission.id,
      menuPermissionName:
        permission
          .menuPermissionName
          ?.trim(),
      permissionKey:
        this.normalizePermissionKey(
          permission.permissionKey
        ),
      sequence:
        permission.sequence !==
          undefined &&
          permission.sequence !== null
          ? Number(
            permission.sequence
          )
          : null,
      menuPermissionDescription:
        permission
          .menuPermissionDescription ||
        null
    };
  }

  permissionScopeLabel(
    scope?:
      MenuPermissionScope |
      null
  ): string {
    switch (scope) {
      case 'ADMIN_ONLY':
        return 'Admin Only';

      case 'SYSTEM_ROLES':
        return 'System Roles';

      default:
        return 'All Roles';
    }
  }

  isProtectedScope(
    scope?:
      MenuPermissionScope |
      null
  ): boolean {
    return (
      scope === 'ADMIN_ONLY' ||
      scope === 'SYSTEM_ROLES'
    );
  }

  toBackendStatus(
    status?: string | null
  ): BackendMenuStatus {
    return status
      ?.trim()
      .toUpperCase() ===
      'INACTIVE'
      ? 'INACTIVE'
      : 'ACTIVE';
  }

  toUiStatus(
    status?: string | null
  ): UiMenuStatus {
    return this.toBackendStatus(
      status
    ) === 'ACTIVE'
      ? 'Active'
      : 'Inactive';
  }

  normalizeMenuKey(
    value: string
  ): string {
    return (value || '')
      .trim()
      .toUpperCase()
      .replace(
        /[^A-Z0-9]+/g,
        '_'
      )
      .replace(
        /_+/g,
        '_'
      )
      .replace(
        /^_+|_+$/g,
        ''
      );
  }

  normalizePermissionKey(
    value: string
  ): string {
    return this.normalizeMenuKey(
      value
    );
  }

  private resolveParentName(
    menu: Menu,
    names: Map<string, string>
  ): string | null {
    if (menu.parentName) {
      return menu.parentName;
    }

    if (
      menu.parentMenu &&
      !this.looksLikeUuid(
        menu.parentMenu
      )
    ) {
      return menu.parentMenu;
    }

    return menu.parentMenuId
      ? names.get(
        menu.parentMenuId
      ) || null
      : null;
  }

  private looksLikeUuid(
    value: string
  ): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      .test(value);
  }
}